/**
 * Vendor portal — Payouts.
 * Shows lifetime sales/payout totals, recent paid orders, and a "Open Stripe
 * dashboard" button (or sandbox notice).
 */
import { useEffect, useState } from "react";
import { useVendor } from "@/lib/store/useVendor";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { formatPrice } from "@/store/storeCart";
import { ExternalLink, Sparkles, Wallet } from "lucide-react";

type PayoutRow = {
  id: string;
  order_number: string;
  total_cents: number;
  vendor_payout_cents: number;
  platform_fee_cents: number;
  refunded_cents: number;
  transfer_created_at: string | null;
  created_at: string;
  status: string;
};

const VendorPayouts = () => {
  const { vendor, isSandbox } = useVendor();
  const { toast } = useToast();
  const [rows, setRows] = useState<PayoutRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (!vendor) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("store_orders")
        .select(
          "id, order_number, total_cents, vendor_payout_cents, platform_fee_cents, refunded_cents, transfer_created_at, created_at, status",
        )
        .eq("vendor_id", vendor.id)
        .in("status", ["paid", "fulfilled", "shipped", "delivered", "partially_refunded", "refunded"])
        .order("created_at", { ascending: false })
        .limit(50);
      if (cancelled) return;
      setRows((data as PayoutRow[]) ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [vendor]);

  const openStripe = async () => {
    setLinking(true);
    const { data, error } = await supabase.functions.invoke("store-vendor-express-login", {
      body: {},
    });
    setLinking(false);
    if (error || !data) {
      toast({ title: "Error", description: error?.message ?? "Could not open dashboard.", variant: "destructive" });
      return;
    }
    if (data.sandbox) {
      toast({ title: "Sandbox vendor", description: data.message });
      return;
    }
    if (data.url) window.open(data.url, "_blank");
  };

  if (!vendor) return null;

  const totalEarned = rows.reduce((s, r) => s + r.vendor_payout_cents, 0);
  const totalRefunded = rows.reduce((s, r) => s + r.refunded_cents, 0);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Payouts</h2>
        <p className="text-sm text-muted-foreground">
          Track your earnings and manage your payout account.
        </p>
      </div>

      {isSandbox && (
        <Alert>
          <Sparkles className="h-4 w-4" />
          <AlertTitle>Sandbox vendor</AlertTitle>
          <AlertDescription>
            You're in sandbox mode — Stripe Connect is bypassed and no real payouts will be made.
            Numbers below are simulated.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Lifetime sales</p>
          <p className="text-2xl font-bold mt-2">{formatPrice(vendor.total_sales_cents)}</p>
          <p className="text-xs text-muted-foreground mt-1">{vendor.total_orders} orders</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Earned (recent 50)
          </p>
          <p className="text-2xl font-bold mt-2 text-primary">{formatPrice(totalEarned)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Refunded</p>
          <p className="text-2xl font-bold mt-2 text-destructive">{formatPrice(totalRefunded)}</p>
        </Card>
      </div>

      <Card className="p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Wallet className="h-5 w-5 text-primary" />
          <div>
            <p className="font-semibold">Stripe dashboard</p>
            <p className="text-sm text-muted-foreground">
              {isSandbox
                ? "Not available in sandbox mode."
                : vendor.stripe_charges_enabled
                  ? "Manage your bank account, view payouts, and download statements."
                  : "Finish Stripe setup from the Settings page first."}
            </p>
          </div>
        </div>
        <Button
          onClick={openStripe}
          disabled={linking || isSandbox || !vendor.stripe_charges_enabled}
        >
          {linking ? "Opening…" : "Open Stripe"} <ExternalLink className="ml-2 h-4 w-4" />
        </Button>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold mb-3">Recent transactions</h3>
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No transactions yet.</p>
        ) : (
          <div className="divide-y divide-border text-sm">
            <div className="grid grid-cols-12 gap-2 py-2 text-xs uppercase tracking-wider text-muted-foreground">
              <span className="col-span-4">Order</span>
              <span className="col-span-2 text-right">Total</span>
              <span className="col-span-2 text-right">Fee</span>
              <span className="col-span-2 text-right">Refunded</span>
              <span className="col-span-2 text-right">Payout</span>
            </div>
            {rows.map((r) => (
              <div key={r.id} className="grid grid-cols-12 gap-2 py-2 items-center">
                <div className="col-span-4 min-w-0">
                  <p className="font-medium truncate">{r.order_number}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString()}{" "}
                    <Badge variant="secondary" className="ml-1 capitalize text-[10px]">
                      {r.status.replace(/_/g, " ")}
                    </Badge>
                  </p>
                </div>
                <span className="col-span-2 text-right">{formatPrice(r.total_cents)}</span>
                <span className="col-span-2 text-right text-muted-foreground">
                  − {formatPrice(r.platform_fee_cents)}
                </span>
                <span className="col-span-2 text-right text-destructive">
                  {r.refunded_cents > 0 ? `− ${formatPrice(r.refunded_cents)}` : "—"}
                </span>
                <span className="col-span-2 text-right font-semibold text-primary">
                  {formatPrice(r.vendor_payout_cents - r.refunded_cents)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default VendorPayouts;
